import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Table from '@cloudscape-design/components/table';
import Header from '@cloudscape-design/components/header';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Container from '@cloudscape-design/components/container';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Grid from '@cloudscape-design/components/grid';
import Button from '@cloudscape-design/components/button';
import LineChart from '@cloudscape-design/components/line-chart';
import { StatsCard } from '../components/StatsCard';
import { getSessionHistory } from '../services/sessions';
import type { Session, UserStats } from '../types';

/**
 * Calculate user statistics from session data.
 *
 * @param sessions - Array of user sessions
 * @returns UserStats object with calculated values
 */
function calculateStats(sessions: Session[]): UserStats {
  const completedSessions = sessions.filter(
    (session) => session.status === 'completed' && session.score !== null
  );

  const totalScore = completedSessions.reduce(
    (sum, session) => sum + (session.score ?? 0),
    0
  );

  const averageScore =
    completedSessions.length > 0
      ? Math.round(totalScore / completedSessions.length)
      : 0;

  return {
    averageScore,
    sessionsCompleted: completedSessions.length,
    totalSessions: sessions.length,
  };
}

/**
 * Format a date string for display in the session history table.
 *
 * @param dateString - ISO date string
 * @returns Formatted date string (e.g., "Jan 15, 2024")
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a score for display, handling null values.
 *
 * @param score - Score value or null
 * @returns Formatted score string (e.g., "85" or "N/A")
 */
function formatScore(score: number | null): string {
  return score !== null ? String(score) : 'N/A';
}

/**
 * Dashboard Page - User statistics and session history
 *
 * Displays the user's training progress including:
 * - Stat cards showing Average Score, Sessions Completed, and Total Sessions
 * - Session history table with Date, Scenario, Role, and Score columns
 * - Row click navigation to evaluation view for completed sessions
 *
 * Features:
 * - Sessions sorted by date (most recent first) per Requirement 9.4
 * - Clickable rows navigate to /evaluation/:sessionId
 * - Uses Cloudscape Design System components
 *
 * @validates Requirements 9.1 - Display stat cards showing Average Score, Sessions Completed, and Total Sessions
 * @validates Requirements 9.2 - Display a session history table
 * @validates Requirements 9.3 - Allow clicking on a session row to navigate to the evaluation view
 * @validates Requirements 9.4 - Show the user's most recent sessions first
 */
export function DashboardPage(): React.ReactElement {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSessions, setSelectedSessions] = useState<Session[]>([]);

  // Fetch sessions on mount
  useEffect(() => {
    async function fetchSessions() {
      try {
        setLoading(true);
        setError(null);
        const data = await getSessionHistory();
        setSessions(data);
      } catch (err) {
        console.error('Failed to fetch sessions:', err);
        setError('Failed to load session history');
      } finally {
        setLoading(false);
      }
    }
    fetchSessions();
  }, []);

  // Sort sessions by date (most recent first) to satisfy Requirement 9.4
  const sortedSessions = useMemo(() => {
    return [...sessions].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [sessions]);

  // Calculate user statistics from session data
  const stats = useMemo(() => calculateStats(sortedSessions), [sortedSessions]);

  /**
   * Handle viewing the selected session's evaluation.
   */
  const handleViewEvaluation = (): void => {
    if (selectedSessions.length === 0) return;
    const session = selectedSessions[0];
    navigate(`/evaluation/${session.id}`);
  };

  return (
    <ContentLayout
      data-testid="dashboard-page"
      header={
        <Header
          variant="h1"
          description="View your training progress and session history"
        >
          Dashboard
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* Stats Cards Section */}
        <Grid
          gridDefinition={[
            { colspan: { default: 12, s: 4 } },
            { colspan: { default: 12, s: 4 } },
            { colspan: { default: 12, s: 4 } },
          ]}
        >
          <StatsCard
            title="Average Score"
            value={stats.averageScore}
            description="Based on completed sessions"
          />
          <StatsCard
            title="Sessions Completed"
            value={stats.sessionsCompleted}
            description="Successfully finished"
          />
          <StatsCard
            title="Total Sessions"
            value={stats.totalSessions}
            description="All sessions started"
          />
        </Grid>

        {/* Score Progression Chart */}
        {sortedSessions.filter(s => s.score !== null).length > 1 && (
          <Container header={<Header variant="h2">Score Progression</Header>}>
            <LineChart
              series={[
                {
                  title: 'Overall Score',
                  type: 'line',
                  data: [...sortedSessions]
                    .filter(s => s.score !== null)
                    .reverse()
                    .map((s, i) => ({
                      x: i + 1,
                      y: s.score ?? 0,
                    })),
                },
              ]}
              yDomain={[0, 100]}
              xScaleType="linear"
              xTitle="Session"
              yTitle="Score"
              height={250}
              hideFilter
              empty={<Box textAlign="center">No scored sessions yet</Box>}
            />
          </Container>
        )}

        {/* Session History Table */}
        <Table
          data-testid="session-history-table"
          loading={loading}
          loadingText="Loading sessions..."
          columnDefinitions={[
            {
              id: 'date',
              header: 'Date',
              cell: (item: Session) => formatDate(item.date),
              sortingField: 'date',
              width: 150,
            },
            {
              id: 'scenario',
              header: 'Scenario',
              cell: (item: Session) => item.scenarioName,
              sortingField: 'scenarioName',
              width: 300,
            },
            {
              id: 'role',
              header: 'Role',
              cell: (item: Session) => item.role,
              sortingField: 'role',
              width: 200,
            },
            {
              id: 'score',
              header: 'Score',
              cell: (item: Session) => formatScore(item.score),
              sortingField: 'score',
              width: 100,
            },
          ]}
          items={sortedSessions}
          trackBy="id"
          variant="container"
          selectionType="single"
          selectedItems={selectedSessions}
          onSelectionChange={({ detail }) => setSelectedSessions(detail.selectedItems)}
          header={
            <Header
              variant="h2"
              description="Select a session and click View Evaluation"
              counter={`(${sortedSessions.length})`}
              actions={
                <Button
                  variant="primary"
                  disabled={selectedSessions.length === 0}
                  onClick={handleViewEvaluation}
                >
                  View Evaluation
                </Button>
              }
            >
              Session History
            </Header>
          }
          empty={
            <Box
              margin={{ vertical: 'xs' }}
              textAlign="center"
              color="inherit"
            >
              <SpaceBetween size="m">
                <b>{error ? 'Error loading sessions' : 'No sessions yet'}</b>
                <Box variant="p" color="inherit">
                  {error || 'Complete a training session to see your history here.'}
                </Box>
              </SpaceBetween>
            </Box>
          }
        />
      </SpaceBetween>
    </ContentLayout>
  );
}

export default DashboardPage;
